import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BseDto,
  InviteInput,
  NegotiationInviteDto,
  NegotiationRoundDto,
  OrganizationInviteDto,
  OrganizationType,
  PersonDto,
  useEditNegotiationRoundMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { OrganizationTypeTag } from "../../../../common/organization/OrganizationTypeTag";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { displayPersonNameWithEmail } from "../../../contracts/view-single/sharing/AddExternalParticipantModal";

interface NewInviteInput {
  input: InviteInput;
  organizationName: string;
}

export const InvitesTable = ({ event, negotiationRound }: { event: BseDto; negotiationRound: NegotiationRoundDto }) => {
  const { t } = useTranslation();
  const confirmationModal = useConfirmationModal();
  const displayer = useApiError();
  const [editNegotiationRound] = useEditNegotiationRoundMutation();
  const editInviteModal = useDisclosure();
  const createInviteModal = useDisclosure();
  const [currentInvite, setCurrentInvite] = useState<NegotiationInviteDto | null>(null);
  const [newInvite, setNewInvite] = useState<NewInviteInput | null>(null);

  const requiresRemovalConfirmation = (): boolean => negotiationRound.stateName === "Published";

  const getNegotiationInviteByOrganizationId = (organizationId: string) =>
    negotiationRound.invites.find((i) => i.organizationId === organizationId);

  const removeNegotiationInvite = async (inviteId: string) => {
    await editNegotiationRound({
      negotiationId: negotiationRound.id,
      editNegotiationRequest: {
        inviteIdToRemove: inviteId,
      },
    })
      .unwrap()
      .catch((e) => displayer.trigger(e));
  };

  const addNegotiationInvite = async (inviteInput: InviteInput) => {
    await editNegotiationRound({
      negotiationId: negotiationRound.id,
      editNegotiationRequest: {
        inviteInput: {
          organizationId: inviteInput.organizationId,
          personIds: inviteInput.personIds,
          message: inviteInput.message ?? "",
        },
      },
    }).unwrap();
  };

  const updateNegotiationInviteMessage = async ({ inviteId, message }: { inviteId: string; message: string }) => {
    await editNegotiationRound({
      negotiationId: negotiationRound.id,
      editNegotiationRequest: {
        setInviteMessage: {
          inviteId,
          message,
        },
      },
    }).unwrap();
  };

  const openNegotiationInviteMessageModal = (orgInvite: OrganizationInviteDto) => {
    const existingNegotiationInvite = getNegotiationInviteByOrganizationId(orgInvite.organizationId);
    if (existingNegotiationInvite) {
      setCurrentInvite(existingNegotiationInvite);
      editInviteModal.onOpen();
      return;
    }
    setNewInvite({
      input: {
        organizationId: orgInvite.organizationId,
        personIds: orgInvite.persons.map((p) => p.id),
        message: "",
      },
      organizationName: orgInvite.organizationName,
    });
    createInviteModal.onOpen();
  };

  const saveExistingInviteMessage = async () => {
    if (!currentInvite) return;
    const performUpdate = () =>
      updateNegotiationInviteMessage({ inviteId: currentInvite.id, message: currentInvite.message ?? "" })
        .then(() => setCurrentInvite(null))
        .catch((e) => displayer.trigger(e));
    if (negotiationRound.stateName === "Published") {
      confirmationModal.show({
        title: t("Confirm update"),
        description: t(
          "You are live, and the selected organization will immediately receive the updated invite message"
        ),
        onConfirmed: performUpdate,
        confirmButtonColor: "blue",
        confirmButtonText: t("Save") ?? "",
      });
    } else {
      await performUpdate();
    }
  };

  const createNewInviteWithMessage = async () => {
    if (!newInvite) return;
    const performCreate = () =>
      addNegotiationInvite({
        organizationId: newInvite.input.organizationId,
        personIds: newInvite.input.personIds,
        message: newInvite.input.message ?? "",
      })
        .then(() => setNewInvite(null))
        .catch((e) => displayer.trigger(e));
    if (negotiationRound.stateName === "Published") {
      confirmationModal.show({
        title: t("Confirm invite"),
        description: t("You are live, and the selected organization will immediately be invited to the event"),
        onConfirmed: performCreate,
        confirmButtonColor: "blue",
        confirmButtonText: t("Create") ?? "",
      });
    } else {
      await performCreate();
    }
  };

  const handleRemoveNegotiationInvite = async (inviteId?: string) => {
    if (!inviteId) return;
    if (requiresRemovalConfirmation()) {
      confirmationModal.show({
        title: t("Confirm removal"),
        description: t("You are live, and the selected organizations wil immediately be removed from the event"),
        onConfirmed: () => removeNegotiationInvite(inviteId),
        confirmButtonColor: "blue",
        confirmButtonText: t("Send") ?? "",
      });
    } else removeNegotiationInvite(inviteId);
  };

  return (
    <Box
      height="full"
      width="full"
      maxWidth="full"
      overflowX={{ base: "scroll", xl: "hidden" }}
      border="1px solid"
      borderColor="smBorder"
      rounded="md"
      pb="2.5"
    >
      {confirmationModal.modal}

      {/* Edit existing negotiation invite */}
      <Modal isOpen={!!currentInvite} onClose={() => setCurrentInvite(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Edit Invitation Message")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {currentInvite && (
              <>
                <Text mb="2">
                  {t("Message to")}: {currentInvite.organizationName}
                </Text>
                <Textarea
                  value={currentInvite.message ?? ""}
                  onChange={(e) => setCurrentInvite({ ...currentInvite, message: e.target.value })}
                  placeholder={`${t("Enter message for the organization")}`}
                  rows={5}
                />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={() => setCurrentInvite(null)}>
              {t("Cancel")}
            </Button>
            <Button
              colorScheme="blue"
              onClick={saveExistingInviteMessage}
              isDisabled={
                !currentInvite ||
                currentInvite.message ===
                  (getNegotiationInviteByOrganizationId(currentInvite.organizationId)?.message ?? "")
              }
            >
              {t("Save Message")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create new negotiation invite */}
      <Modal isOpen={!!newInvite} onClose={() => setNewInvite(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Add Invitation Message")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {newInvite && (
              <>
                <Text mb="2">
                  {t("Message to")}: {newInvite.organizationName}
                </Text>
                <Textarea
                  value={newInvite.input.message ?? ""}
                  onChange={(e) =>
                    setNewInvite({ ...newInvite, input: { ...newInvite.input, message: e.target.value } })
                  }
                  placeholder={`${t("Enter message for the organization")}`}
                  rows={5}
                />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={() => setNewInvite(null)}>
              {t("Cancel")}
            </Button>
            <Button colorScheme="blue" onClick={createNewInviteWithMessage}>
              {t("Create invite")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Table variant="simple" width="full">
        <Thead>
          <Tr>
            <Th>{t("Organization name")}</Th>
            <Th>{t("Type")}</Th>
            <Th>{t("Recipients")}</Th>
            <Th>{t("Message")}</Th>
            <Th>{t("Invited")}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {event.organizationInvites.map((orgInvite: OrganizationInviteDto) => {
            const negotiationInviteForOrg = getNegotiationInviteByOrganizationId(orgInvite.organizationId);
            return (
              <InviteRow
                key={orgInvite.id}
                organizationInvite={orgInvite}
                hasNegotiationInvite={!!negotiationInviteForOrg}
                negotiationInviteId={negotiationInviteForOrg?.id ?? undefined}
                negotiationInviteMessage={negotiationInviteForOrg?.message ?? undefined}
                onOpenNegotiationInviteMessageModal={openNegotiationInviteMessageModal}
                onRemoveNegotiationInvite={handleRemoveNegotiationInvite}
              />
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

type InviteRowProps = {
  organizationInvite: OrganizationInviteDto;
  hasNegotiationInvite: boolean;
  negotiationInviteId?: string;
  negotiationInviteMessage?: string;
  onOpenNegotiationInviteMessageModal: (invite: OrganizationInviteDto) => void;
  onRemoveNegotiationInvite: (inviteId?: string) => void;
};

const InviteRow = ({
  organizationInvite,
  hasNegotiationInvite,
  negotiationInviteId,
  negotiationInviteMessage,
  onOpenNegotiationInviteMessageModal,
  onRemoveNegotiationInvite,
}: InviteRowProps) => {
  const { t } = useTranslation();

  const handleMessageClick = () => {
    onOpenNegotiationInviteMessageModal(organizationInvite);
  };

  const handleToggleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    if (!isChecked) {
      if (negotiationInviteId) onRemoveNegotiationInvite(negotiationInviteId);
    } else {
      onOpenNegotiationInviteMessageModal(organizationInvite);
    }
  };

  const renderPerson = (person: PersonDto) => (
    <Text key={person.id} fontSize="sm">
      {displayPersonNameWithEmail(person)}
    </Text>
  );

  return (
    <Tr>
      <Td verticalAlign="top">
        <Text>{organizationInvite.organizationName}</Text>
      </Td>
      <Td verticalAlign="top">
        <OrganizationTypeTag type={OrganizationType.Organization} />
      </Td>
      <Td verticalAlign="top">{organizationInvite.persons.map(renderPerson)}</Td>
      <Td verticalAlign="top">
        {hasNegotiationInvite ? (
          <Tooltip label={negotiationInviteMessage ?? undefined}>
            <Box display="flex" alignItems="center" gap="2">
              <Box fontSize="sm" cursor="pointer" onClick={handleMessageClick}>
                {negotiationInviteMessage && negotiationInviteMessage.length > 0 ? (
                  negotiationInviteMessage.length > 30 ? (
                    `${negotiationInviteMessage.substring(0, 30)}...`
                  ) : (
                    negotiationInviteMessage
                  )
                ) : (
                  <Button size="sm" color="smMuted">
                    {t("Add message")}
                  </Button>
                )}
              </Box>
            </Box>
          </Tooltip>
        ) : (
          <Button fontSize="sm" color="smMuted" fontStyle="italic" cursor="pointer" onClick={handleMessageClick}>
            {t("Add message")}
          </Button>
        )}
      </Td>
      <Td verticalAlign="top" textAlign="center">
        <Switch
          aria-label="toggle invite"
          variant="ghost"
          isChecked={hasNegotiationInvite}
          onChange={handleToggleChange}
        />
      </Td>
    </Tr>
  );
};
